import {useOutletContext} from "react-router-dom";
import {usePlantationFieldStatus} from "plantation/hooks";
import {theme} from "Theme";

import {FormContainer} from "base/form/components";
import {SectionCard} from "base/ui/section/components";
import {DialogLayout} from "base/dialog/components";
import {Spinner} from "base/shared/components";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DateUtil from "base/format/utilities/DateUtil";

const ViewPlantationFieldSubPage = () => {
    const context = useOutletContext();
    const plantation = context[0];

    const {
        fieldStatus,
        forcedImport,
        handleExportSubmit,
        handleImportSubmit,
        handleForcedImportSubmit,
        enableForcedImport,
        isLoading,
        isDialogOpen,
        setIsDialogOpen,
        merginData,
    } = usePlantationFieldStatus(plantation);

    const getFieldStatusContent = () => {
        switch (fieldStatus) {
            case "not_exported":
                return {
                    statusTitle: "1. Enviar o plantio para a equipa de terreno",
                    statusInfo: (
                        <>
                            <Typography variant="body2">
                                Você vai enviar os dados do plantio para que a equipa
                                possa aceder a eles desde o telemóvel.
                            </Typography>
                            <Typography variant="body2" style={{marginTop: 10}}>
                                Após a exportação, os dados podem ser modificados no
                                terreno e depois importados de volta para o sistema.
                            </Typography>
                        </>
                    ),
                    statusAlert:
                        "Verifique se os dados do plantio estão correctos antes de continuar.",
                    alertSeverity: "info",
                    actionButton: {
                        text: "Exportar para terreno",
                        color: "primary",
                        onConfirm: handleExportSubmit,
                        dialogTitle: "Exportar plantio para terreno",
                        dialogText:
                            "Tem certeza de que deseja exportar este plantio para a aplicação móvel?",
                    },
                };

            case "exported_not_changed":
                return {
                    statusTitle: "2. Continue sem os dados de terreno",
                    statusInfo: (
                        <>
                            <Typography variant="body2">
                                O plantio foi enviado para o telemóvel, mas os dados de
                                terreno ainda não foram actualizados.
                            </Typography>
                            <Typography variant="body2" style={{marginTop: 10}}>
                                Você pode continuar e
                                <Typography
                                    component="span"
                                    variant="body2"
                                    fontWeight="bold"
                                >
                                    {" "}
                                    importar sem dados de terreno{" "}
                                </Typography>
                                ou esperar que a equipa actualize os dados no telemóvel.
                            </Typography>
                        </>
                    ),
                    statusAlert:
                        "Se tiver certeza de que deseja continuar sem os dados de terreno, acrescente o código do plantio.",
                    alertSeverity: "warning",
                    actionButton: {
                        text: "Continuar sem os dados de terreno",
                        color: "warning",
                        disabled: !forcedImport,
                        onConfirm: handleForcedImportSubmit,
                        dialogTitle: "Importar sem dados de terreno",
                        dialogText:
                            "Tem certeza de que deseja importar este plantio sem os dados actualizados no terreno?",
                    },
                    input: (
                        <TextField
                            id="plantation-code-field"
                            label="Código do plantio"
                            variant="outlined"
                            onChange={event => enableForcedImport(event.target.value)}
                            sx={{maxWidth: 400}}
                        />
                    ),
                };

            case "exported_and_changed":
                return {
                    statusTitle:
                        "2. Importe os dados de terreno registrados no telemóvel",
                    statusInfo: (
                        <>
                            <Typography variant="body2">
                                Os dados de terreno já foram acrescentados no telemóvel.
                                Agora você pode importar os dados de terreno.
                            </Typography>
                            {merginData && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: "grey.50",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        Informações de auditoria do terreno:
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Criado por:</strong>{" "}
                                        {merginData.mergin_created_by}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Criado em:</strong>{" "}
                                        {DateUtil.formatDate(
                                            merginData.mergin_created_at
                                        )}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Modificado por:</strong>{" "}
                                        {merginData.mergin_updated_by}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Última modificação:</strong>{" "}
                                        {DateUtil.formatDate(
                                            merginData.mergin_updated_at
                                        )}
                                    </Typography>

                                    {merginData.plant_count !== undefined && (
                                        <Typography variant="body2">
                                            <strong>Plantas registradas:</strong>{" "}
                                            {merginData.plant_count}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </>
                    ),
                    statusAlert:
                        "Os dados de terreno já foram acrescentados. Clique no botão abaixo para importá-los.",
                    alertSeverity: "info",
                    actionButton: {
                        text: "Importar dados de terreno",
                        color: "primary",
                        onConfirm: handleImportSubmit,
                        dialogTitle: "Importar dados de terreno",
                        dialogText:
                            "Tem certeza de que deseja importar os dados de terreno? Esta ação irá sobrescrever os dados actuais.",
                    },
                };

            case "imported":
            case "forced_imported":
                return {
                    statusTitle: "3. Plantio sincronizado com terreno",
                    statusInfo: (
                        <>
                            <Typography variant="body2">
                                O plantio foi importado do terreno pelo menos uma vez.
                                Pode exportar novamente para enviar actualizações à
                                aplicação móvel.
                            </Typography>
                        </>
                    ),
                    statusAlert:
                        "O plantio já foi sincronizado. Pode exportar novamente se necessário.",
                    alertSeverity: "info",
                    actionButton: {
                        text: "Re-exportar para terreno",
                        color: "primary",
                        onConfirm: handleExportSubmit,
                        dialogTitle: "Re-exportar plantio",
                        dialogText:
                            "Tem certeza de que deseja re-exportar este plantio? Isto irá sobrescrever os dados actuais na aplicação móvel.",
                    },
                };

            default:
                return {
                    statusAlert: "Estado do processo desconhecido.",
                    alertSeverity: "error",
                };
        }
    };

    const content = getFieldStatusContent();
    const actionButton = content?.actionButton;

    const handleButtonClick = () => {
        setIsDialogOpen(true);
    };

    const handleConfirm = () => {
        if (actionButton?.onConfirm) {
            actionButton.onConfirm().finally(() => setIsDialogOpen(false));
        }
    };

    return (
        <Stack spacing={1}>
            <FormContainer>
                <SectionCard>
                    <Stack spacing={3} alignItems="center" px={4} pt={2} pb={4}>
                        {isLoading || fieldStatus === null ? (
                            <Spinner />
                        ) : (
                            <>
                                {content.statusTitle && (
                                    <Typography
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontWeight: 500,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {content.statusTitle}
                                    </Typography>
                                )}

                                {content.statusInfo && (
                                    <Box sx={{width: "100%", maxWidth: 600}}>
                                        {content.statusInfo}
                                    </Box>
                                )}

                                {content.statusInfo && content.statusAlert && (
                                    <Divider sx={{width: "100%", my: 2}} />
                                )}

                                {content.statusAlert && (
                                    <Alert
                                        // @ts-ignore - dynamic severity from content config
                                        severity={content.alertSeverity || "info"}
                                        variant="outlined"
                                        sx={{width: "100%", maxWidth: 600}}
                                    >
                                        {content.statusAlert}
                                    </Alert>
                                )}

                                {content.input && (
                                    <Box sx={{width: "100%", maxWidth: 600, mt: 2}}>
                                        {content.input}
                                    </Box>
                                )}

                                {actionButton && (
                                    <Button
                                        variant="contained"
                                        // @ts-ignore - dynamic color from action button config
                                        color={actionButton.color || "primary"}
                                        onClick={handleButtonClick}
                                        disabled={actionButton.disabled || isLoading}
                                        sx={{width: "fit-content", mt: 3}}
                                    >
                                        {actionButton.text}
                                    </Button>
                                )}
                            </>
                        )}
                    </Stack>
                </SectionCard>
            </FormContainer>

            {actionButton && (
                <DialogLayout
                    dialogTitle={actionButton.dialogTitle}
                    dialogContentText={actionButton.dialogText}
                    onMainActionClick={handleConfirm}
                    onHandleDialog={() => setIsDialogOpen(false)}
                    isDialogOpen={isDialogOpen}
                    mainActionColor={actionButton.color || "primary"}
                />
            )}
        </Stack>
    );
};

export default ViewPlantationFieldSubPage;
